import { useCreateUser } from '../../hooks/admin/useAdminMutations'
import { useAdminStore } from '../../stores/adminStore'
import { AdminCreateUserDto, OrgDto } from '../../types/admin-types'
import OrgAutocomplete from './OrgAutocomplete'
import React, { useState, useEffect } from 'react'

// Assuming UserTimeZones enum exists and is imported, otherwise use string
// import { UserTimeZones } from '../../../common/enums/user-timezones.enum'; // Adjust path
const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'UTC',
    // Add other common timezones as needed
]

const AddUserModal: React.FC = () => {
    const isOpen = useAdminStore((state) => state.isAddUserModalOpen)
    const closeModal = useAdminStore((state) => state.closeAddUserModal)
    const { mutate: createUser, isPending, isError, error, isSuccess, reset } = useCreateUser()

    const [formData, setFormData] = useState<Partial<AdminCreateUserDto>>({
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        userOrgName: '',
        orgId: undefined,
        defaultTimezone: timezones[0],
        linkedinUrl: '',
    })
    const [selectedOrg, setSelectedOrg] = useState<OrgDto | null>(null)
    const [allowCustomOrgName, setAllowCustomOrgName] = useState<boolean>(false)

    useEffect(() => {
        if (!isOpen || isSuccess) {
            setFormData({
                firstName: '',
                lastName: '',
                title: '',
                email: '',
                userOrgName: '',
                orgId: undefined,
                defaultTimezone: timezones[0],
                linkedinUrl: '',
            })
            setSelectedOrg(null)
            setAllowCustomOrgName(false)
            reset()
        }
        if (isSuccess) {
            closeModal()
            // Consider adding a success toast notification here
        }
    }, [isOpen, isSuccess, reset, closeModal])

    useEffect(() => {
        if (!allowCustomOrgName && selectedOrg) {
            setFormData((prev) => ({
                ...prev,
                userOrgName: selectedOrg.name,
            }))
        } else if (!selectedOrg) {
            if (!allowCustomOrgName) {
                setFormData((prev) => ({ ...prev, userOrgName: '' }))
            }
        }
    }, [selectedOrg, allowCustomOrgName])

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
        const { name, value, type } = e.target

        if (type === 'checkbox') {
            const target = e.target as HTMLInputElement
            const isChecked = target.checked
            setAllowCustomOrgName(isChecked)
            if (!isChecked && selectedOrg) {
                setFormData((prev) => ({ ...prev, userOrgName: selectedOrg.name }))
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }))
        }
    }

    const handleOrgChange = (org: OrgDto | null) => {
        setSelectedOrg(org)
        setFormData((prev) => ({
            ...prev,
            orgId: org?.id,
        }))
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (
            !formData.firstName ||
            !formData.lastName ||
            !formData.email ||
            !formData.userOrgName ||
            !formData.orgId ||
            !formData.defaultTimezone
        ) {
            alert('Please fill in all required fields.')
            return
        }
        const payload: AdminCreateUserDto = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            userOrgName: formData.userOrgName,
            orgId: formData.orgId,
            defaultTimezone: formData.defaultTimezone,
            ...(formData.title && { title: formData.title }),
            ...(formData.linkedinUrl && { linkedinUrl: formData.linkedinUrl }),
        }
        createUser(payload)
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h2 className="text-xl font-semibold">Add New User</h2>
                    <button
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-600"
                        disabled={isPending}
                    >
                        {/* Close Icon */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label
                            htmlFor="firstName"
                            className="block text-sm font-medium text-gray-700"
                        >
                            First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="lastName"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                            Title (Optional)
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="linkedinUrl"
                            className="block text-sm font-medium text-gray-700"
                        >
                            LinkedIn Profile URL (Optional)
                        </label>
                        <input
                            type="url"
                            id="linkedinUrl"
                            name="linkedinUrl"
                            value={formData.linkedinUrl}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="https://www.linkedin.com/in/username"
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label htmlFor="orgId" className="block text-sm font-medium text-gray-700">
                            Licensing Organization <span className="text-red-500">*</span>
                        </label>
                        <OrgAutocomplete
                            value={formData.orgId}
                            onChange={handleOrgChange}
                            className="mt-1"
                            disabled={isPending}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="userOrgName"
                            className="block text-sm font-medium text-gray-700"
                        >
                            User's Company Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 flex items-center space-x-2">
                            <input
                                type="text"
                                id="userOrgName"
                                name="userOrgName"
                                value={formData.userOrgName}
                                onChange={handleInputChange}
                                className={`flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                                    !allowCustomOrgName ? 'bg-gray-100' : ''
                                }`}
                                required
                                disabled={isPending || !allowCustomOrgName}
                            />
                            <div className="flex items-center h-5">
                                <input
                                    id="allowCustomOrgName"
                                    name="allowCustomOrgName"
                                    type="checkbox"
                                    checked={allowCustomOrgName}
                                    onChange={handleInputChange}
                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    disabled={isPending}
                                />
                            </div>
                            <label
                                htmlFor="allowCustomOrgName"
                                className="text-sm text-gray-600 whitespace-nowrap"
                            >
                                Edit
                            </label>
                        </div>
                        {!allowCustomOrgName && selectedOrg && (
                            <p className="mt-1 text-xs text-gray-500">
                                Defaults to selected Licensing Organization ({selectedOrg.name}).
                                Check box to edit.
                            </p>
                        )}
                        {!allowCustomOrgName && !selectedOrg && (
                            <p className="mt-1 text-xs text-gray-500">
                                Select a Licensing Organization first or check box to enter
                                manually.
                            </p>
                        )}
                    </div>
                    <div>
                        <label
                            htmlFor="defaultTimezone"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Default Timezone <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="defaultTimezone"
                            name="defaultTimezone"
                            value={formData.defaultTimezone}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            disabled={isPending}
                        >
                            {timezones.map((tz) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </select>
                    </div>
                    {isError && (
                        <div className="text-red-600 text-sm">
                            Error creating user: {/* Now we can safely access error.message */}
                            {error?.message || 'Unknown error'}
                        </div>
                    )}
                    <div className="pt-4 border-t flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                            disabled={isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isPending}
                        >
                            {isPending ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddUserModal
